import {
    Chart,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
} from "chart.js/auto";
import { useEffect, useRef, useState } from "react";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import { convertDateFormat } from '../../../../scripts/utils.js';
import Loader from "../../../../components/Loader";
import { useTranslation } from "react-i18next";

export default function ChartsWebex({ dataForChart, allLabels, allDates, labelTitle, typeOfChart, titleChart, date, handleGraphClick, mesurementUnit, theme }) {
    const chartRef = useRef();
    Chart.register(
        Title,
        Tooltip,
        Legend,
        CategoryScale,
        LinearScale,
        BarElement,
        TimeScale,
    );

    const { t } = useTranslation();
    const [chartDataDoughnut, setChartDataDoughnut] = useState({});
    const [optionsDoughnut, setOptionsDoughnut] = useState({});
    const [chartDataBar, setChartDataBar] = useState({});
    const [optionsBar, setOptionsBar] = useState({});
    const [chartData, setChartData] = useState({});
    const [options, setOptions] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const colors = {
        [t("Redirection-d'appel")]: "#997adb",
        [t("Call redirection")]: "#997adb",
        [t("Oui")]: "#AEC353",
        [t("Non")]: "#124188",
        [t("Yes")]: "#AEC353",
        [t("No")]: "#124188",
        [t("Remote")]: "#997adb",
        [t("Interne")]: "#997adb",
        [t("National")]: "#CC8C6E",
        [t("Entrant")]: "#AEC353",
        [t("Sortant")]: "#997adb",
        [t("Incoming")]: "#AEC353",
        [t("Outgoing")]: "#997adb",
        [t("Mobile")]: "#EB5C5C",
        [t("Prenium")]: "#FFE033",
        [t("Opérateur")]: "#EAE1B2",
        [t("Sans frais")]: "#004299",
        [t("Inconnu")]: "#e3e4ed",
        [t("International")]: "#55C3FC",
        [t("Réunion")]: "#124188",
        [t("Courbevoie")]: "#997adb",
        [t("Sortant - Nombre d'appels répondus")]: "rgba(170 130 255 / 50%)",
        [t("Sortant - Nombre d'appels non répondus")]: "rgba(170 130 255 / 50%)",
        [t("Sortant - Durée totale des appels sortants")]: "rgba(170 130 255 / 50%)",
        [t("Entrant - Nombre d'appels répondus")]: "rgba(174 195 83 / 50%)",
        [t("Entrant - Nombre d'appels non répondus")]: "rgba(174 195 83 / 50%)",
        [t("Entrant - Durée totale des appels entrants")]: "rgba(174 195 83 / 50%)",
        [t("Total - Durée totale des appels")]: "rgba(101, 175, 232, 0.5)",
        [t("Total - Réponse totale des appels")]: "rgba(101, 175, 232, 0.5)",
        [t("Mis hors service")]: "#FF6B6B",
        [t("Infecté")]: "#FF8C76",
        [t("En ligne")]: "#2ECC71",
        [t("Obsolète")]: "#FFC154",
        [t("À jour")]: "#FFD748",
        [t("En cours")]: "#FF9E66",
        [t("Malveillant et non résolu")]: "#FF8C66",
        [t("Non atténué")]: "#FF7A45",
        [t("Non atténué et non résolu")]: "#FF6B22",
        [t("Non résolu")]: "#FF5700",
        [t("Résolu")]: "#2ECC71",
        [t("Suspect, non atténué et non résolu")]: "#FFC466",
        [t("Suspect et non résolu")]: "#FFD54F",
    };

    const verticalLinePlugin = {
        id: 'verticalLine',
        beforeDraw: (chart) => {
            const tooltip = chart.tooltip;
            if (tooltip && tooltip._active && tooltip._active.length) {
                const ctx = chart.ctx;
                const activePoint = tooltip._active[0];
                const x = activePoint.element.x;
                const yStart = activePoint.element.y;
                const yEnd = chart.chartArea.bottom;
                const strokeColor = typeOfChart === "trends-line" || typeOfChart === "trends-bar" ?  colors[titleChart] : theme === "dark" ? "rgba(153, 122, 219, 1)" : "rgba(18,65,136, 0.7)";
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(x, yStart);
                ctx.lineTo(x, yEnd);
                ctx.lineWidth = 1;
                ctx.strokeStyle = strokeColor;
                ctx.setLineDash([4, 4]);
                ctx.stroke();
                ctx.restore();
            }
        }
    };

    const textCenter = {
        id: 'textCenter',
        beforeDatasetsDraw(chart) {
            const { ctx, data, chartArea: { left, right, top, bottom } } = chart;
            ctx.save();
            const meta = chart.getDatasetMeta(0);
            const visibleData = data.datasets[0].data.filter((value, index) => !meta.data[index].hidden);
            const total = visibleData.reduce((acc, value) => acc + value, 0);
            const secondLineText = `${total}`;
            ctx.font = '35px sans-serif';
            ctx.fillStyle = theme === "dark" ? "#ffffff" : "#303030";
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const centerX = (left + right) / 2;
            const centerY = (top + bottom) / 2;
            ctx.fillText(secondLineText, centerX, centerY - 10);
            const firstLineText = 'Total';
            ctx.font = '20px sans-serif';
            ctx.fillText(firstLineText, centerX, centerY + 20);
            ctx.restore();
        }
    };

    const getDifferentShade = (baseColor, variation) => {
        const color = parseInt(baseColor.substring(1), 16);
        let r = (color >> 16) & 0xFF;
        let g = (color >> 8) & 0xFF;
        let b = color & 0xFF;
        r = (r + variation * 50) % 255;
        g = (g + variation * 70) % 255;
        b = (b + variation * 90) % 255;
        return `rgb(${r}, ${g}, ${b})`;
    };
    
    const createChart = () => {
        if (typeOfChart === "doughnut") {
            const defaultColors = ['#00BCEB', '#005072', '#6EBE4A'];
            const labels = allLabels;
            const newColors = labels.map((label, index) => {
                if (index < defaultColors.length) {
                    return defaultColors[index];
                }
                const baseColor = defaultColors[index % defaultColors.length];
                const variation = Math.floor(index / defaultColors.length);
                return getDifferentShade(baseColor, variation);
            });
            const datasets = [
                {
                    label: typeof labelTitle !== 'string' ? labels.map(label => labelTitle[label][1] || 0) : t(`${labelTitle}`),
                    data: typeof labelTitle !== 'string' ? labels.map(label => dataForChart[label][0] || 0) : labels.map(label => dataForChart[label] || 0),
                    backgroundColor: typeof labelTitle !== 'string' ? newColors : labels.map(label => colors[label]),
                    hoverBackgroundColor: typeof labelTitle !== 'string' ? newColors : labels.map(label => colors[label]),
                    borderColor: theme === "dark" ? "transparent" : "#ffffff",
                    borderWidth: 2,
                    untranslatedLabel: labelTitle
                }
            ];
            setChartDataDoughnut({ labels, datasets });
            setOptionsDoughnut({
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'right',
                        labels: {
                            padding: 15,
                            color: theme === "dark" ? "#ffffff" : "#303030",
                            font: {
                                family: "Inter",
                                size: 11,
                                weight: 700
                            },
                            usePointStyle: true,
                        },
                        onClick: (e, legendItem, legend) => {
                            const index = legendItem.index;
                            const chart = legend.chart;
                            const meta = chart.getDatasetMeta(0);
                            meta.data[index].hidden = !meta.data[index].hidden;
                            chart.update();
                        }
                    },
                    textCenter: {
                        enabled: true,
                    },
                    tooltip: {
                        callbacks: {
                            label: function (context) {
                                if (typeof labelTitle !== 'string') {
                                    let label = [];
                                    const currentLabelData = dataForChart[context.label];
                                    if (currentLabelData) {
                                        label.push(`${currentLabelData[1]} : ${currentLabelData[0]}`);
                                    }
                                    return label
                                }
                            }
                        },
                        displayColors: false
                    }
                },
                hover: {
                    mode: null,
                },
                cutout: '75%',
                animation: {
                    animateRotate: true,
                    animateScale: true,
                },
            });
        } else if (typeOfChart === "bar") {
            const labels = dataForChart ? Object.keys(dataForChart)
                .filter((item) => item !== "")
                .sort((a, b) => new Date(a) - new Date(b))
                .map((key) => t[key] || key) : [];
            const datasets = allLabels.map((label) => ({
                label: label,
                data: labels.map((name) => dataForChart[name][label] || 0),
                borderRadius: 0,
                borderColor: colors[label],
                backgroundColor: colors[label],
                untranslatedLabel: labelTitle
            }));
            setChartDataBar({ labels, datasets });
            setOptionsBar({
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: false,
                        text: t(titleChart),
                        align: "center",
                        color: theme === "dark" ? "#ffffff" : "#303030",
                        font: {
                            family: "Inter",
                            size: 12,
                        },
                    },
                    legend: {
                        position: "bottom",
                        labels: {
                            padding: 10,
                            color: theme === "dark" ? "#ffffff" : "#303030",
                            font: {
                                family: "Inter",
                                size: 11,
                                weight: 700
                            },
                            usePointStyle: true,
                        },
                    },
                    tooltip: {
                        callbacks: {
                            title: function (context) {
                                if (!date || !date[0] || !date[1]) {
                                    return "";
                                }
                                const starttime = convertDateFormat(date[0]);
                                const endtime = convertDateFormat(date[1]);
                                return `${starttime} - ${endtime}`;
                            },
                            label: function (context) {
                                let label = [];
                                const currentLabelData = dataForChart[context.label];
                                Object.keys(currentLabelData).forEach(key => {
                                    if (key !== context.dataset.label) {
                                        label.push(`${key} : ${currentLabelData[key]} (100.00%)`);
                                    }
                                });
                                const percentage = ((context.raw / currentLabelData.Total) * 100).toFixed(2);
                                label.push(`${context.dataset.label} : ${context.raw} ${percentage === 'NaN' ? "" : '(' + percentage + '%)'}`);
                                return label;
                            }
                        },
                        displayColors: false
                    }
                },
                scales: {
                    x: {
                        display: false,
                        grid: {
                            display: false,
                        },
                    },
                    y: {
                        ticks: {
                            font: {
                                size: 10,
                                family: "Inter",
                            },
                            color: theme === "dark" ? "#ffffff" : "#303030",
                            maxTicksLimit: 8,
                        },
                        grid: {
                            display: false,
                        },
                        stacked: true,
                    },
                },
            });
        } else if (typeOfChart === "trends-bar" || typeOfChart === "trends-line") {
            const dataset = {
                label: labelTitle === "Average_Hour" ? t("Moyenne d'appels") : t("Total d'appels"),
                data: allLabels.map((label) => dataForChart[label] || 0),
                borderColor: colors[titleChart],                
                backgroundColor: (context) => {
                    if (typeOfChart === 'trends-line') {
                        const chart = context.chart;
                        const { ctx, chartArea } = chart;
                        if (!chartArea) {
                            return null;
                        }

                        const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
                        const startColor = colors[titleChart];
                        const endColor = colors[titleChart];

                        gradient.addColorStop(0, startColor);
                        gradient.addColorStop(1, endColor);

                        return gradient;
                    }
                    return colors[titleChart];
                },
                fill: typeOfChart === 'trends-line',
                tension: typeOfChart === 'trends-line' ? 0.4 : 0,
                pointRadius: typeOfChart === 'trends-line' ? 0 : 3,
                pointHoverRadius: typeOfChart === 'trends-line' ? 7 : 3,
                pointHoverBackgroundColor: (() => {
                    return colors[titleChart];
                })(),
                pointHoverBorderColor: 'white',
                pointHoverBorderWidth: 2,
            };
            setChartData({
                labels: allLabels,
                datasets: [dataset],
            });
            setOptions({
                responsive: true,
                indexAxis: "x",
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        position: "top",
                        text: t(titleChart),
                        align: "center",
                        padding: 20,
                        color: theme === "dark" ? "#ffffff" : "#303030",
                        font: {
                            family: "Inter",
                            size: "15",
                        },
                    },
                    legend: {
                        display: false,
                        position: "bottom",
                        labels: {
                            padding: 10,
                            color: theme === "dark" ? "#ffffff" : "#303030",
                            font: {
                                family: "Inter",
                                size: 11,
                                weight: 700
                            },
                            usePointStyle: true,
                        },
                    },
                    tooltip: {
                        callbacks: {
                            title: function (context) {
                                if (labelTitle) {
                                    return context[0].dataset.label[context[0].dataIndex];
                                } else {
                                    if (allDates) {
                                        return allDates[context[0].dataIndex];
                                    } else {
                                        return context[0].label;
                                    }
                                }
                            },
                            label: function (context) {
                                const valueInSeconds = context.raw;
                                const hours = Math.floor(valueInSeconds / 3600);
                                const minutes = Math.floor((valueInSeconds % 3600) / 60);
                                const seconds = valueInSeconds % 60;
                                const formattedValue = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                                let label = `${context.label} : ${mesurementUnit === 'formatDuration' ? formattedValue : context.formattedValue} ${mesurementUnit !== 'formatDuration' && mesurementUnit ? '/' + mesurementUnit : ''}`;
                                return label;
                            }
                        },
                        displayColors: false
                    }
                },
                interaction: {
                    intersect: false,
                },
                scales: {
                    x: {
                        ticks: {
                            color: theme === "dark" ? "#ffffff" : "#303030",
                            font: {
                                size: 10,
                                family: "Inter",
                                weight: 800
                            },
                            maxTicksLimit: 8,
                        },
                        grid: {
                            display: false,
                        },
                    },
                    y: {
                        ticks: {
                            color: theme === "dark" ? "#ffffff" : "#303030",
                            font: {
                                size: 10,
                                family: "Inter",
                                weight: 800
                            },
                            callback: function (value) {
                                if (mesurementUnit === 'formatDuration') {
                                    let displayValue;
                                    if (value >= 3600) {
                                        const hours = Math.floor(value / 3600);
                                        const minutes = Math.round((value % 3600) / 60);
                                        displayValue = `${hours}h${minutes}`;
                                    } else if (value >= 60) {
                                        const minutes = Math.floor(value / 60);
                                        displayValue = `${minutes}m`;
                                    } else {
                                        displayValue = `${Math.floor(value)}s`;
                                    }
                                    return displayValue;
                                } else {
                                    return value;
                                }
                            },
                            maxTicksLimit: 10,
                        },
                        grid: {
                            color: theme === "dark" ? "#ffffff" : "#303030",
                            lineWidth: 0.2,
                        },
                    },
                },
            });
        } else if (typeOfChart === "line-bar") {
            const dataset = {
                label: labelTitle === "Average_Hour" ? t("Moyenne d'appels") : t("Total d'appels"),
                data: allLabels.map((label) => dataForChart[label] || 0),
                backgroundColor: theme === "dark" ? "rgba(153, 122, 219, 1)" : "rgba(18,65,136, 0.7)",
                borderColor: theme === "dark" ? "rgba(153, 122, 219, 1)" : "rgba(18,65,136, 0.7)",
                borderWidth: 2,
                pointRadius: 0,
            };
            setChartData({
                labels: allLabels,
                datasets: [dataset],
            });
            setOptions({
                responsive: true,
                indexAxis: "x",
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        position: "top",
                        text: t(titleChart),
                        align: "center",
                        padding: 20,
                        color: theme === "dark" ? "#ffffff" : "#303030",
                        font: {
                            family: "Inter",
                            size: "15",
                        },
                    },
                    legend: {
                        position: "bottom",
                        labels: {
                            padding: 10,
                            color: theme === "dark" ? "#ffffff" : "#303030",
                            font: {
                                family: "Inter",
                                size: 11,
                                weight: 700
                            },
                            usePointStyle: true,
                        },
                    },
                },
                interaction: {
                    intersect: false,
                },
                scales: {
                    x: {
                        ticks: {
                            color: theme === "dark" ? "#ffffff" : "#303030",
                            font: {
                                size: 10,
                                family: "Inter",
                                weight: 800
                            },
                            maxTicksLimit: 6,
                        },
                        grid: {
                            display: false,
                        },
                    },
                    y: {
                        ticks: {
                            color: theme === "dark" ? "#ffffff" : "#303030",
                            font: {
                                size: 10,
                                family: "Inter",
                                weight: 800
                            },
                        },
                        grid: {
                            color: theme === "dark" ? "#ffffff" : "#303030",
                            lineWidth: 0.2,
                        },
                    },
                },
            });
        } else if (typeOfChart === "line") {
            const dataset = {
                label: labelTitle === "Average_Hour" ? t("Moyenne d'appels") : t("Total d'appels"),
                data: allLabels.map((label) => dataForChart[label] || 0),
                borderRadius: 3,
                borderColor: theme === "dark" ? "rgba(153, 122, 219, 1)" : "rgba(18,65,136, 0.7)",
                backgroundColor: (context) => {
                    if (typeOfChart === 'line') {
                        const chart = context.chart;
                        const { ctx, chartArea } = chart;
                        if (!chartArea) {
                            return null;
                        }

                        const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
                        const startColor = theme === "dark" ? 'rgba(104, 104, 104, 0)' : 'rgba(255, 255, 255, 0)';
                        const endColor = theme === "dark" ? 'rgba(153, 122, 219, 1)' : 'rgba(18, 65, 136, 0.15)';

                        gradient.addColorStop(0, startColor);
                        gradient.addColorStop(1, endColor);

                        return gradient;
                    }
                    return colors[label];
                },
                fill: typeOfChart === 'line',
                tension: typeOfChart === 'line' ? 0.4 : 0,
                pointRadius: typeOfChart === 'line' ? 0 : 3,
                pointHoverRadius: typeOfChart === 'line' ? 7 : 3,
                pointHoverBackgroundColor: (() => {
                    return theme === "dark" ? 'rgba(153, 122, 219, 0.87)' : 'rgba(18, 65, 136, 0.87)';
                })(),
                pointHoverBorderColor: 'white',
                pointHoverBorderWidth: 2,
            };
        setChartData({
            labels: allLabels,
            datasets: [dataset],
        });
        setOptions({
            responsive: true,
            indexAxis: "x",
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: true,
                    position: "top",
                    text: t(titleChart),
                    align: "center",
                    padding: 20,
                    color: theme === "dark" ? "#ffffff" : "#303030",
                    font: {
                        family: "Inter",
                        size: "15",
                    },
                },
                legend: {
                    position: "bottom",
                    labels: {
                        padding: 10,
                        color: theme === "dark" ? "#ffffff" : "#303030",
                        font: {
                            family: "Inter",
                            size: 11,
                            weight: 700
                        },
                        usePointStyle: true,
                    },
                },
            },
            interaction: {
                intersect: false,
            },
            scales: {
                x: {
                    ticks: {
                        color: theme === "dark" ? "#ffffff" : "#303030",
                        font: {
                            size: 10,
                            family: "Inter",
                            weight: 800
                        },
                        maxTicksLimit: 6,
                    },
                    grid: {
                        display: false,
                    },
                },
                y: {
                    ticks: {
                        color: theme === "dark" ? "#ffffff" : "#303030",
                        font: {
                            size: 10,
                            family: "Inter",
                            weight: 800
                        },
                    },
                    grid: {
                        color: theme === "dark" ? "#ffffff" : "#303030",
                        lineWidth: 0.2,
                    },
                },
            },
        });
    }
};

useEffect(() => {
    createChart();
    setIsLoading(false);
}, [dataForChart, allLabels]);

return (
    <>
        {!isLoading ? (
            <>
                {typeOfChart === "doughnut" ? (
                    <Doughnut
                        data={chartDataDoughnut}
                        options={optionsDoughnut}
                        ref={chartRef}
                        plugins={[textCenter]}
                        onClick={(event) =>
                            handleGraphClick
                                ? handleGraphClick(event, chartRef.current)
                                : undefined
                        }
                    />
                ) : typeOfChart === "bar" ? (
                    <Bar
                        data={chartDataBar}
                        options={optionsBar}
                        ref={chartRef}
                        onClick={(event) =>
                            handleGraphClick
                                ? handleGraphClick(event, chartRef.current)
                                : undefined
                        }
                    />
                ) : typeOfChart === "trends-bar" ? (
                    <Bar
                        data={chartData}
                        options={options}
                        ref={chartRef}
                    />
                ) : typeOfChart === "trends-line" ? (
                    <Line
                        data={chartData}
                        options={options}
                        ref={chartRef}
                        plugins={[verticalLinePlugin]}
                    />
                ) : typeOfChart === "line-bar" ? (
                    <Bar
                        data={chartData}
                        options={options}
                        ref={chartRef}
                    />
                ) : typeOfChart === "line" && (
                    <Line
                        data={chartData}
                        options={options}
                        ref={chartRef}
                        plugins={[verticalLinePlugin]}
                    />
                )}
            </>
        ) : (
            <Loader />
        )}
    </>
);
}